import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { useCommunityActivity } from "@/domains/certifications/hooks/useCommunityActivity";
import { getWeekByTopic } from "@/domains/certifications/utils";
import { DiscussionActivityProps } from "@/features/certifications/participant-detail/types";
import { CONTRIBUTION_KINDS } from "@/domains/certifications/constants";

import "../styles.css";

const RECENT_ITEMS = 10;

const DiscussionActivity = ({
  certification,
  userId,
}: DiscussionActivityProps) => {
  const { t } = useTranslation();
  const weekByTopic = useMemo(
    () => getWeekByTopic(certification.modules),
    [certification.modules],
  );
  const topicIds = useMemo(() => Array.from(weekByTopic.keys()), [weekByTopic]);
  const { items } = useCommunityActivity({ topicIds, userId });

  if (items.length === 0) {
    return null;
  }

  const posts = items.filter(
    ({ kind }) => kind === CONTRIBUTION_KINDS.POST,
  ).length;
  const comments = items.length - posts;

  return (
    <section className="certification-card participant-detail-card flex flex-col gap-300">
      <header className="flex flex-wrap items-baseline justify-between gap-200">
        <h2 className="certification-heading body-m-medium">
          {t("certification.admin.participant.discussion.title")}
        </h2>

        <span className="certification-muted body-s-regular">
          {t("certification.admin.participant.discussion.summary", {
            posts: t("certification.admin.participant.discussion.posts", {
              count: posts,
            }),
            comments: t("certification.admin.participant.discussion.comments", {
              count: comments,
            }),
          })}
        </span>
      </header>

      <ul className="participant-detail-weeks flex flex-col">
        {items.slice(0, RECENT_ITEMS).map((item) => {
          const week = item.topicIds
            .map((topicId) => weekByTopic.get(topicId))
            .find((value) => value !== undefined);

          return (
            <li
              key={item.key}
              className="participant-detail-week flex flex-col gap-100"
            >
              <div className="flex flex-wrap items-center gap-200 body-xs-regular">
                <span className="participant-detail-kind">
                  {t(`certification.admin.participant.discussion.${item.kind}`)}
                </span>

                {week !== undefined && (
                  <span className="certification-muted">
                    {t("certification.labels.week", { position: week })}
                  </span>
                )}

                <time dateTime={item.createdAt} className="certification-muted">
                  {DateTime.fromISO(item.createdAt).toRelative()}
                </time>

                {item.isUnderReview && (
                  <span className="participant-detail-open">
                    {t(
                      "certification.admin.participant.discussion.underReview",
                    )}
                  </span>
                )}
              </div>

              <p className="participant-detail-text body-s-regular">
                {item.text}
              </p>

              <Link
                to={`/discussions/${item.discussionId}`}
                className="certification-link body-xs-regular"
              >
                {t(
                  `certification.admin.participant.discussion.open.${item.kind}`,
                  {
                    title: item.discussionTitle,
                  },
                )}
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default DiscussionActivity;
